import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'projects/service/src/environments/environment';

// "Impersonate" in the user menu, for QA on Dev / Test / Stage (Martin, 2026-11-02; environment.impersonation, never
// Prod). RIMS API (api/Impersonation) gets a short-lived token (WebAuth:Impersonation:TokenMinutes) for the picked user from WebAuth; the app swaps it in and
// reloads. The admin's own token waits in sessionStorage (this tab only; Sign out's localStorage.clear() leaves it), so
// Stop, the automatic stop before expiry, or a 401 with the impersonation token brings the admin back.
// The token never goes in a URL.

const SESSION_KEY = 'rims.impersonation';
const SWITCHED_KEY = 'rims.impersonation.switched';
const API = `${environment.apiUrl}Impersonation/`;

export interface ImpersonationSession {
  adminToken: string;
  userName: string;
  displayName: string;
  expiration: string;
}

interface StartResponse {
  accessToken: string;
  expiration: string;
  userName: string;
  displayName: string;
}

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly http = inject(HttpClient);

  readonly enabled = !!environment.impersonation;
  readonly session = signal<ImpersonationSession | null>(ImpersonationService.readSession());

  // Null: no menu entry (not a test environment, or not a RIMS admin). Otherwise the entry shows; a message says what
  // is missing when the server is not set up yet (its WebAuth client).
  async available(): Promise<{ available: boolean; message?: string } | null> {
    if (!this.enabled) return null;
    try {
      return await firstValueFrom(this.http.get<{ available: boolean; message?: string }>(`${API}available`));
    } catch {
      return null;
    }
  }

  // Starts impersonating userName. Resolves only on failure (the page reloads on success); the message says why.
  async start(userName: string): Promise<string> {
    const adminToken = localStorage.getItem('access_token');
    if (!adminToken) return 'You are not signed in.';
    try {
      const res = await firstValueFrom(this.http.post<StartResponse>(`${API}start`, { userName }));
      const session: ImpersonationSession = {
        adminToken, userName: res.userName, displayName: res.displayName || res.userName, expiration: res.expiration,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      ImpersonationService.switchTo(res.accessToken);
      return '';
    } catch (e: any) {
      return e?.error?.message || 'Impersonation could not start. Try again, or tell the RIMS team.';
    }
  }

  // Ends the session (WebAuth records it) and goes back to the admin's own token.
  async stop(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${API}stop`, {}));
    } catch {
      // Expired or refused: the admin goes back anyway.
    }
    ImpersonationService.restoreAdmin();
  }

  // At start-up (app initializer), before anything reads the token. Goes back to the admin's token when the
  // impersonation token is gone (a 401 signed the impersonated user out) or expired. True when the token in use changed
  // since the last page (a start, a stop or this restore): the app then loads the user record again.
  static onStartup(): boolean {
    const switched = sessionStorage.getItem(SWITCHED_KEY) === '1';
    sessionStorage.removeItem(SWITCHED_KEY);
    const session = ImpersonationService.readSession();
    if (!session) return switched;
    const token = localStorage.getItem('access_token');
    if (token && !ImpersonationService.isImpersonationToken(token)) {
      // Signed in again meanwhile (a sign-in link): the saved session is stale.
      sessionStorage.removeItem(SESSION_KEY);
      return switched;
    }
    if (token && !ImpersonationService.expired(token)) return switched;
    sessionStorage.removeItem(SESSION_KEY);
    ImpersonationService.useToken(session.adminToken);
    return true;
  }

  // Where a failed sign-in sends the browser (home's user load, the global error handler): back to the admin's own token
  // while impersonating (the impersonation token is dropped; onStartup restores the admin), else the login app.
  static signInUrl(): string {
    if (!ImpersonationService.readSession()) return environment.loginAppUrl;
    localStorage.removeItem('access_token');
    return '/home';
  }

  // Sign out while impersonating ends everything: the admin's token is not restored.
  static forget(): void {
    sessionStorage.removeItem(SESSION_KEY);
  }

  private static restoreAdmin(): void {
    const session = ImpersonationService.readSession();
    sessionStorage.removeItem(SESSION_KEY);
    if (!session) return;
    ImpersonationService.switchTo(session.adminToken);
  }

  private static switchTo(token: string): void {
    ImpersonationService.useToken(token);
    sessionStorage.setItem(SWITCHED_KEY, '1');
    window.location.href = '/home';
  }

  // The same reset as signing in with a token (initApp): the stored user record, role claims and ids belong to the
  // previous user; the app initializer loads the new user's record.
  private static useToken(token: string): void {
    localStorage.clear();
    localStorage.setItem('access_token', token);
  }

  private static readSession(): ImpersonationSession | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private static claims(token: string): any {
    try {
      return jwtDecode(token);
    } catch {
      return {};
    }
  }

  private static isImpersonationToken(token: string): boolean {
    return !!ImpersonationService.claims(token)?.impersonation;
  }

  private static expired(token: string): boolean {
    const exp = ImpersonationService.claims(token)?.exp;
    return !exp || exp * 1000 <= Date.now();
  }
}
